// @flow

import { useMemo } from 'react';

import { CornerDownRight } from 'lucide-react';

import {
  useDiscountDetails,
  selectOneProductById,
  type ProductError,
} from '@gandi/cart';

import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks';

import { ListRow } from '../../../ListRow';

import { useBestCatalogPrice } from '../../modules/catalog';

import ProductItemBase from './ProductItemBase';
import { TitleRow } from './Rows';
import { DiscountRow } from './Rows/DiscountRow';
import ErrorRow from './Rows/ErrorRow';
import TotalRow from './Rows/TotalRow';
import { useRemoveProductHandler } from './modules';

export type Props = {
  /** do no show delete button */
  deleteDisabled?: boolean,

  /** required to be able to delete the product */
  productId: void | string | number,

  /**
   * Product title
   */
  label: React.Node,

  /**
   * Product additional info (arrow, bold)
   */
  detail?: React.Node,

  /**
   * Product additional info (secondary, weak color, below the `detail` arrow)
   */
  info?: ?string,

  /** show a corporate icon */
  isCorporate?: boolean,

  /** show the total row */
  priceBeforeTaxes?: ?number,
  hasTaxes?: boolean,

  /** first content, usually the `TitleRow` */
  header?: React.Node,
  /** specific content, to display just after the title */
  children?: React.Node,
  listRow?: React.Node,
  /** content shown last, in place of the `TotalRow`; usually a `DurationRow` */
  footer?: React.Node,
  errors?: Array<ProductError> | React.MixedElement,
};

export default function ProductItem({
  priceBeforeTaxes,
  hasTaxes,

  isCorporate,

  deleteDisabled,
  detail,
  label,
  productId,
  info,

  header,
  children,
  footer,

  errors,

  listRow: ListRowComponent,
}: Props): React.MixedElement {
  const productIdProps = useMemo(() => ({ productId }), [productId]);
  const removeProduct = useRemoveProductHandler(productId);
  const product = useAppSelector(state =>
    // $FlowFixMe[incompatible-call] - productId can be undefined, but then no discount to display
    selectOneProductById(state, productIdProps),
  );
  // $FlowFixMe[incompatible-call]
  const discountDetails = useDiscountDetails({ product });
  const bestCatalogPrice = useBestCatalogPrice(productId || '');

  const errorElement = Array.isArray(errors)
    ? errors.length > 0 && (
        <ErrorRow handleRemoveAction={removeProduct} errors={errors} />
      )
    : errors;

  return (
    <ErrorBoundary fallback={<ProductItemBase.Error product={product} />}>
      <ProductItemBase
        header={
          <>
            <TitleRow
              deleteDisabled={deleteDisabled}
              isCorporate={isCorporate}
              handleRemoveAction={removeProduct}
              label={label}
              productType={
                product?.payload.shopping?.studio
                  ? 'studio'
                  : product?.productType
              }
            />
            {errorElement}
            {header}
          </>
        }
        footer={
          footer ||
          (typeof priceBeforeTaxes === 'number' && (
            <TotalRow
              hasTaxes={hasTaxes}
              totalPrice={priceBeforeTaxes}
              hasDiscount={!!discountDetails?.length}
              priceCrossedOut={bestCatalogPrice.priceCrossedOut}
            />
          ))
        }
      >
        {children}
        {ListRowComponent ||
          (!!detail && (
            <ListRow
              intent="muted"
              icon={<CornerDownRight size={16} />}
              title={detail}
              options={info ? [info] : undefined}
              optionsArrow={false}
            />
          ))}
        {discountDetails?.map(discount => (
          <DiscountRow
            // $FlowFixMe[incompatible-type] - productId can be undefined, but then no discount to display
            key={`discount-${productId}-${discount.label}`}
            label={discount.label}
            price={discount.price}
          />
        ))}
      </ProductItemBase>
    </ErrorBoundary>
  );
}
